/**
 * Admin API functions for user management
 * Used by admin users to manage allowed DataEntry users
 */

import apiClient from "../../services/apiClient";
import { dataLoaderUri } from "../API";

// Types for admin user management
export interface AllowedUser {
  email: string;
  role: 'admin' | 'editor' | 'viewer';
  name: string;
  enabled: boolean;
  addedBy: string;
  addedAt: string;
  lastAccessAt?: string;
}

export interface NewUser {
  email: string;
  name: string;
  role: 'admin' | 'editor' | 'viewer';
}

export interface UserUpdates {
  role?: 'admin' | 'editor' | 'viewer';
  name?: string;
  enabled?: boolean;
}

export interface CurrentUserInfo {
  email: string;
  role: 'admin' | 'editor' | 'viewer';
  name: string;
  enabled: boolean;
}

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  retryableStatusCodes: [503, 502, 504, 429],
};

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

async function withRetry<T>(
  apiCall: () => Promise<T>,
  operation: string = "API call"
): Promise<T> {
  let lastError: any;

  for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
    try {
      const result = await apiCall();
      if (attempt > 0) {
        console.log(`✅ ${operation} succeeded on attempt ${attempt + 1}`);
      }
      return result;
    } catch (error: any) {
      lastError = error;

      let isRetryable = false;
      let statusCode = 0;

      if (error.response) {
        statusCode = error.response.status;
        isRetryable = RETRY_CONFIG.retryableStatusCodes.includes(statusCode);
      } else if (!error.response && error.message) {
        // Network error
        isRetryable = true;
      }

      if (!isRetryable || attempt === RETRY_CONFIG.maxRetries) {
        break;
      }

      const delay = Math.min(
        RETRY_CONFIG.baseDelay * Math.pow(2, attempt),
        RETRY_CONFIG.maxDelay
      );

      console.warn(`⚠️ ${operation} failed on attempt ${attempt + 1}. Retrying in ${delay}ms...`);
      await sleep(delay);
    }
  }

  console.error(`❌ ${operation} failed after ${RETRY_CONFIG.maxRetries + 1} attempts`);
  throw lastError;
}

/**
 * Fetch the current user's role and info
 * Any authenticated user can call this
 * Note: We use skipAuthErrorHandler because a 401 here means the user is not
 * in the allowed users table - we want this error to bubble up, not trigger
 * the refresh token flow.
 */
export async function fetchCurrentUserInfo(): Promise<CurrentUserInfo> {
  return withRetry(async () => {
    // Use axios instance directly to set skipAuthErrorHandler header
    const axiosInstance = apiClient.getAxiosInstance();
    const response = await axiosInstance.get<{ user: AllowedUser }>(
      `${dataLoaderUri}/api/v1/admin/users`,
      {
        params: { me: 'true' },
        headers: { skipAuthErrorHandler: true }
      }
    );
    const user = response.data.user;
    return {
      email: user.email,
      role: user.role,
      name: user.name,
      enabled: user.enabled,
    };
  }, "fetchCurrentUserInfo");
}

/**
 * Fetch all allowed users
 * Admin only
 */
export async function fetchAllowedUsers(): Promise<AllowedUser[]> {
  return withRetry(async () => {
    const response = await apiClient.get<{ users: AllowedUser[] }>(
      `${dataLoaderUri}/api/v1/admin/users`
    );
    return response.users;
  }, "fetchAllowedUsers");
}

/**
 * Fetch a specific user by email
 * Admin only (or self)
 */
export async function fetchUserByEmail(email: string): Promise<AllowedUser> {
  return withRetry(async () => {
    const response = await apiClient.get<{ user: AllowedUser }>(
      `${dataLoaderUri}/api/v1/admin/users`,
      { params: { email } }
    );
    return response.user;
  }, `fetchUserByEmail(${email})`);
}

/**
 * Add a new allowed user
 * Admin only
 */
export async function addAllowedUser(user: NewUser): Promise<AllowedUser> {
  return withRetry(async () => {
    const response = await apiClient.post<{ message: string; user: AllowedUser }>(
      `${dataLoaderUri}/api/v1/admin/users`,
      {
        email: user.email,
        name: user.name,
        role: user.role,
      }
    );
    console.log(`✅ User added: ${user.email}`);
    return response.user;
  }, `addAllowedUser(${user.email})`);
}

/**
 * Update an existing user's role, name, or enabled status
 * Admin only
 */
export async function updateAllowedUser(
  email: string,
  updates: UserUpdates
): Promise<AllowedUser> {
  return withRetry(async () => {
    const response = await apiClient.put<{ message: string; user: AllowedUser }>(
      `${dataLoaderUri}/api/v1/admin/users`,
      {
        email,
        ...updates,
      }
    );
    console.log(`✅ User updated: ${email}`);
    return response.user;
  }, `updateAllowedUser(${email})`);
}

/**
 * Delete an allowed user
 * Admin only
 */
export async function deleteAllowedUser(email: string): Promise<void> {
  return withRetry(async () => {
    await apiClient.request({
      method: 'delete',
      url: `${dataLoaderUri}/api/v1/admin/users`,
      data: { email },
    });
    console.log(`✅ User deleted: ${email}`);
  }, `deleteAllowedUser(${email})`);
}

/**
 * Toggle a user's enabled status
 * Admin only (convenience wrapper)
 */
export async function toggleUserEnabled(
  email: string,
  enabled: boolean
): Promise<AllowedUser> {
  return updateAllowedUser(email, { enabled });
}

/**
 * Change a user's role
 * Admin only (convenience wrapper)
 */
export async function changeUserRole(
  email: string,
  role: 'admin' | 'editor' | 'viewer'
): Promise<AllowedUser> {
  return updateAllowedUser(email, { role });
}
